/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import PropTypes from 'util/PropTypes';
import classnames from 'classnames';
import { SVGIcon, Button, TruncatedText } from '@ba/ba-ui-carbon-toolkit';

import _ from 'underscore';

import './toolbarItem.scss';

class ToolbarItem extends Component {

	constructor(props) {
		super(props);
	}

	render() {
		if (this.props.hidden) {
			return null;
		}

		const forwardedProps = _.omit(this.props, 'children', 'onPrimaryAction', 'enabled', 'selected', 'hidden', 'icon', 'iconId', 'label', 'className', 'kind');

		const classes = classnames({
			'explore-vis-toolbar__item': true,
			'explore-vis-toolbar__item--selected': this.props.selected,
			'explore-vis-toolbar__item--enabled': this.isEnabled(),
			'explore-vis-toolbar__item--actionable': this.isActionable(),
			[`${this.props.className}`]: this.props.className
		});

		const icon = this.renderIcon(this.props.icon) || this.renderIconId(this.props.iconId);
		const label = this.renderLabel(this.props.label);
		const tabIndex = this.isActionable() ? 0 : -1;
		/* use ariaLabel in props if it is defined. Otherwise use title
		when both of them are falsy, react is smart enough not to render an undefined attribute */
		const ariaLabel = this.props.ariaLabel || this.props.title;

		const id = this.props.id;

		if(this.props.kind === 'ghost'){
			return (
				<Button
					className={this.props.selected? 'selected': ''}
					data-id={id}
					walkme-data-id={id}
					kind="ghost"
					tabIndex={tabIndex}
					icon={icon}
					intent={'selection'}
					size='field' //it has 40px as height
					onMouseDown={(...params) => this.performAction(...params)}
					onTouchStart={(...params) => this.performAction(...params)}
					// ↓ on mobile to suppress the default behavior of a tap - eliminate the superfluous mousedown event
					onTouchEnd={event => event.preventDefault()}
					onKeyPress={(...params) => this.onKeyPress(...params)}
					{...forwardedProps}
				>
					<TruncatedText value={this.props.label}/>
					{this.props.children}
				</Button>
			);
		} else {
			// @todo: migrate the rest of the buttons to carbon button
			return (
				<div
					className={classes}
					onMouseDown={(...params) => this.performAction(...params)}
					onTouchStart={(...params) => this.performAction(...params)}
					// ↓ on mobile to suppress the default behavior of a tap - eliminate the superfluous mousedown event
					onTouchEnd={event => event.preventDefault()}
					onKeyPress={(...params) => this.onKeyPress(...params)}
					tabIndex={tabIndex}
					aria-label={ariaLabel}
					data-id={id}
					walkme-data-id={id}
					{...forwardedProps}
				>
					{label}
					{icon}
					{this.props.children}
				</div>
			);
		}
	}

	renderIcon(icon) {
		if (!icon) {
			return;
		}

		return (
			<div className="explore-vis-toolbar__item__icon">
				{icon}
			</div>
		);
	}

	renderIconId(id) {
		if (!id) {
			return;
		}

		return this.renderIcon(
			<SVGIcon iconId={id} />
		);
	}

	renderLabel(text) {
		if (!text) {
			return;
		}

		return (
			<div className="explore-vis-toolbar__item__text">
				{text}
			</div>
		);
	}

	isEnabled() {
		return this.props.enabled;
	}

	isActionable() {
		return this.isEnabled() && this.props.onPrimaryAction;
	}

	onEnter(event) {
		this.performAction(event);
	}

	onKeyPress(event) {
		const handler = {
			'Enter': () => this.onEnter(event)
		}[event.key];

		handler && handler();
	}

	performAction(event) {
		if (this.isActionable()) {
			this.props.onPrimaryAction(event);
		}
	}

}

ToolbarItem.propTypes = {
	children: PropTypes.any,
	className: PropTypes.string,
	icon: PropTypes.object,
	iconId: PropTypes.string,
	ariaLabel: PropTypes.string,
	label: PropTypes.string,
	title: PropTypes.string,
	hidden: PropTypes.bool,
	enabled: PropTypes.bool,
	selected: PropTypes.bool,
	onPrimaryAction: PropTypes.func,
	id: PropTypes.string,
	kind: PropTypes.string
};

ToolbarItem.defaultProps = {
	children: [],
	hidden: false,
	enabled: true,
	selected: false,
	kind: ''
};

export default ToolbarItem;



// WEBPACK FOOTER //
// ./src/js/components/toolbar/items/ToolbarItem.js